import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import StaticActivity from "./StaticActivity";
import WaterActivity from "./WaterActivity";
import DynamicActivity from "./DynamicActivity";
import SaunaActivity from "./SaunaActivity";
import ErrorBoundary from "../ErrorBoundary";
import ResultPanel from "./ResultPanel";

import { calculateActivity, type YogaPose } from "./calculateResult";

import staticIcon from "../../assets/calculator/act_statica.png";
import waterIcon from "../../assets/calculator/act_water.png";
import dynamicIcon from "../../assets/calculator/act_run.png";
import saunaIcon from "../../assets/calculator/act_sauna.png";

import styles from "./Calculator.module.scss";
import { LanguageSwitcher } from "@/components/locale/LanguageSwitcher";

type ActivityType = "static" | "water" | "dynamic" | "sauna" | null;
export type AdjustValueType = (field: string) => void;
export const Calculator: React.FC = () => {
    const { t } = useTranslation();
    const [activity, setActivity] = useState<ActivityType>(null);

    const [saunaTemperature, setSaunaTemperature] = useState(60);
    const [saunaHumidity, setSaunaHumidity] = useState(60);
    const [saunaTime, setSaunaTime] = useState(43);

    const [dynamicTime, setDynamicTime] = useState<number>(120);
    const [dynamicDistance, setDynamicDistance] = useState<number>(17);

    const [waterTemperature, setWaterTemperature] = useState<number>(4);
    const [waterTime, setWaterTime] = useState<number>(5);

    const [staticTime, setStaticTime] = useState<number>(65);
    const [staticPose, setStaticPose] = useState<YogaPose>("diamond");

    const activityRef = useRef<HTMLDivElement>(null);

    const handleActivitySelect = (value: typeof activity) => {
        setActivity(value);
    };

    const adjustValue = (field: string) => {
        switch (activity) {
            case "static":
                if (field === "time") {
                    setStaticTime(findValue("staticTime", 1, 120));
                }
                break;
            case "water":
                if (field === "time") {
                    setWaterTime(findValue("waterTime", 1, 120));
                }
                if (field === "temperature") {
                    setWaterTemperature(findValue("waterTemperature", 1, 40));
                }
                break;
            case "dynamic":
                if (field === "distance") {
                    setDynamicDistance(
                        findValue("dynamicDistance", 1, 30, 0.1),
                    );
                }
                if (field === "time") {
                    setDynamicTime(findValue("dynamicTime", 1, 270));
                }
                break;
            case "sauna":
                if (field === "temperature") {
                    setSaunaTemperature(findValue("saunaTemperature", 40, 120));
                }
                if (field === "humidity") {
                    setSaunaHumidity(findValue("saunaHumidity", 20, 100));
                }
                if (field === "time") {
                    setSaunaTime(findValue("saunaTime", 1, 120));
                }
                break;
        }
    };

    const state = {
        activity,
        saunaTemperature,
        saunaHumidity,
        saunaTime,
        dynamicTime,
        dynamicDistance,
        waterTemperature,
        waterTime,
        staticTime,
        staticPose,
    };

    function findValue(
        property: string,
        start: number,
        end: number,
        step: number = 1,
    ) {
        let bestMajor: number[] = [];
        let bestMinor: number[] = [];
        let value = start;
        while (value <= end) {
            const result = calculateActivity({ ...state, [property]: value }) / 7;

            if (result < 5) {
                if (!bestMinor.length || result > bestMinor[1]) {
                    bestMinor = [value, result];
                }
            } else {
                if (!bestMajor.length || result < bestMajor[1]) {
                    bestMajor = [value, result];
                }
            }
            value += step;
        }
        const bestValue = bestMajor.length ? bestMajor[0] : bestMinor[0];
        const wholeParts = Math.round(1 / step);
        return Number(bestValue.toFixed(wholeParts));
    }

    const result = calculateActivity(state);

    const renderActivityComponent = () => {
        switch (activity) {
            case "static":
                return (
                    <StaticActivity
                        time={staticTime}
                        setTime={setStaticTime}
                        pose={staticPose}
                        setPose={setStaticPose}
                        adjustValue={adjustValue}
                    />
                );
            case "water":
                return (
                    <WaterActivity
                        temperature={waterTemperature}
                        setTemperature={setWaterTemperature}
                        time={waterTime}
                        setTime={setWaterTime}
                        adjustValue={adjustValue}
                    />
                );
            case "dynamic":
                return (
                    <DynamicActivity
                        time={dynamicTime}
                        setTime={setDynamicTime}
                        distance={dynamicDistance}
                        setDistance={setDynamicDistance}
                        adjustValue={adjustValue}
                    />
                );
            case "sauna":
                return (
                    <SaunaActivity
                        temperature={saunaTemperature}
                        setTemperature={setSaunaTemperature}
                        humidity={saunaHumidity}
                        setHumidity={setSaunaHumidity}
                        time={saunaTime}
                        setTime={setSaunaTime}
                        adjustValue={adjustValue}
                    />
                );
            default:
                return null;
        }
    };

    const warnings: string[] = [];

    if (activity === "sauna") {
        if (saunaTemperature + saunaHumidity < 100) {
            warnings.push(t("warnings.lowSauna"));
        } else if (saunaTemperature + saunaHumidity > 120) {
            warnings.push(t("warnings.highSauna"));
        }
    }

    return (
        <div id={`${styles.calcRoot}`}>
            <h2
                style={{ textAlign: "center" }}
                className={activity ? styles.selected : ""}
            >
                {t("select_activity")}
            </h2>
            <div ref={activityRef} className={`${styles.activitySelector}`}>
                <button
                    className={`${styles.activityButton} ${activity === "static" ? styles.active : ""}`}
                    onClick={() => handleActivitySelect("static")}
                >
                    <img src={staticIcon} alt={t("activity.static")} />
                </button>
                <button
                    className={`${styles.activityButton} ${activity === "water" ? styles.active : ""}`}
                    onClick={() => handleActivitySelect("water")}
                >
                    <img src={waterIcon} alt={t("activity.water")} />
                </button>
                <button
                    className={`${styles.activityButton} ${activity === "dynamic" ? styles.active : ""}`}
                    onClick={() => handleActivitySelect("dynamic")}
                >
                    <img src={dynamicIcon} alt={t("activity.dynamic")} />
                </button>
                <button
                    className={`${styles.activityButton} ${activity === "sauna" ? styles.active : ""}`}
                    onClick={() => handleActivitySelect("sauna")}
                >
                    <img src={saunaIcon} alt={t("activity.sauna")} />
                </button>
            </div>
            <ErrorBoundary>
                <div className={styles.activityAll}>
                    {renderActivityComponent()}
                </div>
            </ErrorBoundary>
            <ResultPanel result={result} warnings={warnings} />
        </div>
    );
};
